import { useCallback, useEffect, useState } from 'react';
import ReactSelect from 'react-select';

import { closestCenter, DndContext, SensorDescriptor, SensorOptions } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
	CreateSurvey,
	LanguagesAbbr, Survey, SurveyQuestion, SurveyQuestionOption, SurveyQuestionType, SurveyType
} from '../../../../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import { reactSelectStyles } from '../../../../../general-ui/react-select/react-select-styles';
import TextInput from '../../../../../general-ui/text-input/text';
import { QuizOption } from './quiz-option';
import BasicInput from '../../../../../general-ui/text-input/basic-input';
import { showAlert } from '../../../../../general-ui/alert/alert-service';

interface Props {
	addDefaultQuizOption: () => Promise<SurveyQuestionOption | undefined>;
	handleDeleteQuestion: (question: SurveyQuestion) => void;
	Handlers: { [key: string]: any };
	id: string;
	isNewQuestion?: boolean;
	isOpen: boolean;
	language: LanguagesAbbr;
	question: SurveyQuestion;
	questionErrors: string[];
	sensors: SensorDescriptor<SensorOptions>[];
	setWorkingQuestion: React.Dispatch<React.SetStateAction<SurveyQuestion | null>>;
	setWorkingQuestionTitle: ({ target: { value }, }: React.ChangeEvent<HTMLInputElement>) => void;
	setWorkingSurvey: React.Dispatch<React.SetStateAction<Survey | CreateSurvey | null>>;
	workingQuestion: SurveyQuestion | null;
	workingSurvey: Survey | CreateSurvey | null;
}

const options = [
	{
		label: 'Single Option',
		value: String(SurveyQuestionType.radio),
	},
	{
		label: 'Multi Option',
		value: String(SurveyQuestionType.checkbox),
	}
];

export function QuizQuestionComponent({
	addDefaultQuizOption,
	handleDeleteQuestion,
	Handlers,
	id,
	isOpen,
	isNewQuestion = false,
	language,
	question,
	questionErrors,
	sensors,
	setWorkingQuestion,
	setWorkingQuestionTitle,
	setWorkingSurvey,
	workingQuestion,
	workingSurvey,
}: Props) {
	const [showErrors, setShowErrors] = useState(false);
	const [workingOption, setWorkingOption] = useState<SurveyQuestionOption | null>(null);
	const { attributes, listeners, setActivatorNodeRef, setNodeRef, transform } = useSortable({ id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	const getDefaultOption = useCallback(async () => {
		try {
			const newDefaultOption = await addDefaultQuizOption();
			if (newDefaultOption) setWorkingOption(newDefaultOption);
		} catch (e) {
			console.error(e);
		}
	}, [addDefaultQuizOption]);

	// Always ensure there is a working option on new questions
	useEffect(() => {
		if (!workingOption) {
			getDefaultOption();
		}
	}, [getDefaultOption, isNewQuestion, workingOption]);

	const submitQuizQuestion = () => {
		const validationErrors = Handlers.validateQuizQuestion(workingQuestion);
		if (validationErrors.length) {
			return setShowErrors(true);
		}

		if (workingSurvey && workingQuestion) {
			let questions: SurveyQuestion[];

			if (isNewQuestion) {
				// Adding new
				questions = [...workingSurvey.questions, workingQuestion];
			} else {
				// Edit existing
				questions = workingSurvey.questions.map((q) => {
					return q.survey_question === workingQuestion.survey_question ? workingQuestion : q;
				});
			}

			setWorkingSurvey({ ...workingSurvey, questions });
			setWorkingQuestion(null);
			setWorkingOption(null);
			setShowErrors(false);
		}
	};

	// Discards the working question form
	const cancelQuizQuestion = () => {
		setWorkingQuestion(null);
	};

	const onCheckCorrect = (option: SurveyQuestionOption) => {
		if (!workingQuestion) return;

		const updatedOption = { ...option, is_correct: !option.is_correct };
		const updatedOptions = workingQuestion.options.map((o) => {
			// can only have one correct so all other options need set to
			return option.survey_question_option === o.survey_question_option
				? updatedOption
				: {
					...o, is_correct: false
				};
		});
		const updatedQuestion = {
			...workingQuestion,
			options: updatedOptions,
		};
		setWorkingQuestion(updatedQuestion);
	};

	const handleDelete = (option: SurveyQuestionOption) => {
		if (!workingQuestion) return;

		const updatedOptions = workingQuestion.options.filter((o) => {
			return o.survey_question_option !== option.survey_question_option;
		});
		const updatedQuestion = { ...workingQuestion, options: updatedOptions };
		setWorkingQuestion(updatedQuestion);
	};

	const handleNewOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!workingOption) {
			getDefaultOption();
			showAlert({ message: "Save failed", description: 'Please choose a correct answer before saving.', duration: 3000, type: "error" });
			return;
		}
		setWorkingOption({
			...workingOption,
			label: {
				...workingOption?.label,
				base: e.target.value,
			},
		});
	};

	const setDefaultQuestionType = () => {
		return workingQuestion?.question_type
			? options.find(option => String(option.value) === String(workingQuestion?.question_type))
			: {
				label: 'Single Option',
				value: String(SurveyQuestionType.radio),
			};
	};

	const handleNewOptionKeyDown = ({ key }: React.KeyboardEvent) => {
		if (!workingQuestion || !workingOption) return;

		// On enter, and if the option is valid
		if (key === 'Enter' && workingOption.label.base.trim()) {
			setWorkingQuestion({
				...workingQuestion,
				options: [
					...workingQuestion.options,
					workingOption,
				],
			});

			setWorkingOption(null);
		}
	};

	if (isOpen) {
		return (
			<div className="edit-quiz-question">
				<div className={workingSurvey?.survey_type === SurveyType.quiz ? 'question-row-quiz-input' : 'question-row'}>
					<TextInput
						value={workingQuestion?.title?.[language] as string ?? workingQuestion?.title.base}
						onChange={setWorkingQuestionTitle}
						placeholder="Type your question..."
						label="Question"
					/>
					{workingSurvey?.survey_type !== SurveyType.quiz && <div className="field-group text normal">
						<label id="aria-label" htmlFor="aria-survey-quiz-answer-type-select">Type</label>
						<ReactSelect
							tabSelectsValue={false}
							styles={reactSelectStyles({
								control: {
									borderRadius: '100px',
								},
								dropdownIndicator: {
									color: 'white'
								}

							})}
							aria-labelledby="aria-label"
							inputId="aria-survey-quiz-answer-type-select"
							options={options}
							onChange={(value) => Handlers.setQuestionType(Number(value?.value))}
							defaultValue={setDefaultQuestionType()}
							menuPortalTarget={document.body}
						/>
					</div>}
				</div>
				<div className="edit-quiz-question-subtitle-wrap">
					<span className="edit-quiz-question-subtitle">Options</span>Mark the correct answer
				</div>

				{workingQuestion && workingQuestion.options.length > 0 ? (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={Handlers.handleDragEndOptions}
						modifiers={[restrictToParentElement]}
						autoScroll={false}
					>
						<SortableContext
							items={workingQuestion.options.map(
								(option) =>
									String(option.survey_question_option)
							)}
							strategy={rectSortingStrategy}
						>
							<div className="options-list">
								{workingQuestion.options.map((option, index) => (
									<QuizOption
										Handlers={Handlers}
										id={String(option.survey_question_option)}
										index={index}
										onCheckCorrect={onCheckCorrect}
										option={option}
										key={`survey-option-form-${index}`}
										onDelete={() => handleDelete(option)}
									/>
								))}
							</div>
						</SortableContext>
					</DndContext>
				) : null}
				<div className="survey-quiz-question-option-entry">
					<div className="survey-quiz-question-option-data">
						<BasicInput
							placeholder={'Add option by typing here and pressing "Enter"'}
							margin="none"
							onChange={handleNewOptionChange}
							onKeyDown={handleNewOptionKeyDown}
							autoFocus
							value={workingOption?.label.base ?? ''}
							className="survey-quiz-question-option-input"
						/>
					</div>
				</div>

				{showErrors && questionErrors.length > 0 && (
					<div className="quiz-error">
						{questionErrors.map((error) => <div key={error}>{error}</div>)}
					</div>
				)}
				<div className="edit-quiz-question-done">
					<button
						className="small align-right"
						onClick={cancelQuizQuestion}
					>
						Cancel
					</button>
					<button
						className="small align-right quiz-margin-left"
						onClick={submitQuizQuestion}
					>
						Done
					</button>
				</div>
			</div>
		);
	}

	return (
		<div
			className="survey-question"
			ref={setNodeRef}
			style={style}
		>
			<div
				className="drag-handle"
				ref={setActivatorNodeRef}
				{...attributes}
				{...listeners}
			>
				<Icon
					name={ICONS.DRAG_HANDLE}
					size={18}
					color={COLORS.DEFAULT_GRAY}
				/>
			</div>

			<div className="survey-question-info">
				<div className="survey-question-info-title">
					{question?.title?.[language] ?? question?.title?.base}
				</div>
				<div className="survey-question-info-question-count">
					{question.options.length} options
				</div>
			</div>
			<div className="survey-question-actions">
				<button
					className="survey-question-actions-button button-margin-right"
					onClick={() => Handlers.editQuizQuestion(question)}
				>
					<Icon name={ICONS.EDIT} size={10} color={COLORS.MED_LIGHT_GRAY} />
				</button>
				<button
					onClick={() => handleDeleteQuestion(question)}
					className="survey-question-actions-button"
				>
					<Icon name={ICONS.TRASH} size={10} color={COLORS.MED_LIGHT_GRAY} />
				</button>
			</div>
		</div>
	);
}
